<template>
  <div class="max-w-2xl mx-auto">
    <!-- 頁面標題 -->
    <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow mb-6">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-wood-800">個人資料設定</h1>
        <button 
          @click="goBack" 
          class="text-wood-600 hover:text-wood-800 transition-colors duration-200"
        >
          <i class="fas fa-arrow-left mr-2"></i>返回
        </button>
      </div>
    </div>

    <!-- 個人資料表單 -->
    <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow">
      <form @submit.prevent="updateProfile">
        <!-- 信箱 (只讀) -->
        <div class="mb-6">
          <label class="block text-wood-700 text-sm font-medium mb-2">信箱</label>
          <input 
            type="email" 
            :value="userStore.currentUser?.email" 
            disabled
            class="w-full px-3 py-2 rounded-xl border border-wood-300 bg-wood-50 text-wood-600 cursor-not-allowed"
          />
        </div>

        <!-- 帳號 (UID) -->
        <div class="mb-6">
          <label class="block text-wood-700 text-sm font-medium mb-2">
            帳號 (UID) 
            <span class="text-red-500">*</span>
            <span v-if="isNewUser" class="text-red-500 text-xs ml-1">(新用戶必須設定)</span>
          </label>
          <input 
            type="text" 
            v-model="formData.uid" 
            :disabled="!isNewUser"
            :class="[
              'w-full px-3 py-2 rounded-xl border transition-colors duration-200',
              isNewUser ? 'border-wood-300 focus:border-wood-500 focus:outline-none' : 'border-wood-300 bg-wood-50 text-wood-600 cursor-not-allowed',
              uidError ? 'border-red-500' : ''
            ]"
            placeholder="請輸入唯一的帳號 (只能使用英文字母和數字)"
            @blur="validateUid"
          />
          <div v-if="uidError" class="text-red-500 text-sm mt-1">
            {{ uidError }}
          </div>
          <div v-if="!isNewUser" class="text-wood-600 text-sm mt-1">
            帳號一旦設定後無法更改
          </div>
        </div>

        <!-- 筆名 -->
        <div class="mb-6">
          <label class="block text-wood-700 text-sm font-medium mb-2">
            筆名 
            <span class="text-red-500">*</span>
          </label>
          <input 
            type="text" 
            v-model="formData.nickname" 
            required
            class="w-full px-3 py-2 rounded-xl border border-wood-300 focus:border-wood-500 focus:outline-none"
            placeholder="請輸入您的筆名"
          />
        </div>

        <!-- 頭像設定 -->
        <div class="mb-6">
          <label class="block text-wood-700 text-sm font-medium mb-2">頭像</label>
          
          <!-- 當前頭像預覽 -->
          <div class="mb-4 flex items-center space-x-4">
            <div class="w-16 h-16 rounded-full overflow-hidden bg-wood-100">
              <img 
                :src="previewAvatar || userStore.userAvatar" 
                alt="頭像預覽" 
                class="w-full h-full object-cover"
              />
            </div>
            <div class="text-wood-600 text-sm">
              <p>當前頭像</p>
              <p class="text-xs text-wood-500">建議尺寸：200x200 像素</p>
            </div>
          </div>

          <!-- 頭像選擇選項 -->
          <div class="space-y-4">
            <!-- 上傳圖片 -->
            <div class="flex items-center space-x-3">
              <input 
                type="radio" 
                id="upload-avatar" 
                value="upload" 
                v-model="avatarType"
                class="text-wood-600 focus:ring-wood-500"
              />
              <label for="upload-avatar" class="text-wood-700 text-sm font-medium">
                上傳圖片
              </label>
            </div>
            
            <div v-if="avatarType === 'upload'" class="ml-6">
              <input 
                type="file" 
                ref="fileInput"
                @change="handleFileUpload"
                accept="image/png, image/jpeg, image/jpg"
                class="text-sm text-wood-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-wood-100 file:text-wood-700 hover:file:bg-wood-200"
              />
            </div>

            <!-- 免費頭像圖庫 -->
            <div class="flex items-center space-x-3">
              <input 
                type="radio" 
                id="free-avatar" 
                value="free" 
                v-model="avatarType"
                class="text-wood-600 focus:ring-wood-500"
              />
              <label for="free-avatar" class="text-wood-700 text-sm font-medium">
                選擇免費頭像
              </label>
            </div>
            
            <div v-if="avatarType === 'free'" class="ml-6">
              <div class="grid grid-cols-9 gap-2 max-h-64 overflow-y-auto">
                <div 
                  v-for="avatar in freeAvatars" 
                  :key="avatar.id"
                  @click="selectFreeAvatar(avatar)"
                  :class="[
                    'w-10 h-10 rounded-full cursor-pointer border-2 transition-all duration-200',
                    selectedFreeAvatar?.id === avatar.id ? 'border-wood-500 ring-2 ring-wood-300' : 'border-wood-200 hover:border-wood-400'
                  ]"
                  :title="avatar.name"
                >
                  <img 
                    :src="avatar.url" 
                    :alt="avatar.name"
                    class="w-full h-full object-cover rounded-full"
                  />
                </div>
              </div>
              <p class="text-xs text-wood-500 mt-2">共 {{ freeAvatars.length }} 個頭像可選擇，點擊即可預覽</p>
            </div>
          </div>
        </div>

        <!-- 表單按鈕 -->
        <div class="flex justify-between items-center pt-4 border-t border-wood-200">
          <button 
            type="button" 
            @click="logout"
            class="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-all duration-300 shadow-md"
          >
            <i class="fas fa-sign-out-alt mr-2"></i>登出
          </button>
          
          <div class="flex space-x-3">
            <button 
              type="button" 
              @click="goBack"
              class="px-6 py-2 text-wood-600 hover:text-wood-700 hover:bg-wood-50 rounded-full transition-all duration-300 shadow-md"
            >
              取消
            </button>
            <button 
              type="submit"
              :disabled="!isFormValid || isSubmitting"
              :class="[
                'px-6 py-2 rounded-full transition-all duration-300 transform shadow-lg',
                (isFormValid && !isSubmitting) 
                  ? 'bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-wood-800 hover:scale-105' 
                  : 'bg-wood-300 text-wood-600 cursor-not-allowed'
              ]"
            >
              <i v-if="isSubmitting" class="fas fa-spinner animate-spin mr-2"></i>
              {{ isSubmitting ? '儲存中...' : '儲存資料' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '../stores/user.js'
import { usePostsStore } from '../stores/posts.js'

const userStore = useUserStore()
const postsStore = usePostsStore()

// 表單資料
const formData = ref({
  uid: '',
  nickname: '',
  avatar: null
})

// 狀態變數
const isSubmitting = ref(false)
const uidError = ref('')
const avatarType = ref('upload')
const selectedFreeAvatar = ref(null)
const previewAvatar = ref('')
const fileInput = ref(null)

// 判斷是否為新用戶（沒有設定過 UID）
const isNewUser = computed(() => {
  return !userStore.currentUser?.uid && !formData.value.uid
})

// 表單驗證
const isFormValid = computed(() => {
  return formData.value.uid.trim() !== '' && 
         formData.value.nickname.trim() !== '' && 
         !uidError.value
})

// 免費頭像圖庫 (使用 Robohash 產生 36 個不同風格頭像)
const freeAvatars = ref([
  // 機器人系列 (set1) - 12個
  { id: 1, name: '藍色機器人', url: 'https://robohash.org/taichi1?set=set1&size=200x200&bgset=bg1' },
  { id: 2, name: '紅色機器人', url: 'https://robohash.org/taichi2?set=set1&size=200x200&bgset=bg2' },
  { id: 3, name: '綠色機器人', url: 'https://robohash.org/taichi3?set=set1&size=200x200&bgset=bg1' },
  { id: 4, name: '黃色機器人', url: 'https://robohash.org/taichi4?set=set1&size=200x200&bgset=bg2' },
  { id: 5, name: '紫色機器人', url: 'https://robohash.org/taichi5?set=set1&size=200x200&bgset=bg1' },
  { id: 6, name: '橙色機器人', url: 'https://robohash.org/taichi6?set=set1&size=200x200&bgset=bg2' },
  { id: 7, name: '粉色機器人', url: 'https://robohash.org/taichi7?set=set1&size=200x200&bgset=bg1' },
  { id: 8, name: '青色機器人', url: 'https://robohash.org/taichi8?set=set1&size=200x200&bgset=bg2' },
  { id: 9, name: '白色機器人', url: 'https://robohash.org/taichi9?set=set1&size=200x200&bgset=bg1' },
  { id: 10, name: '黑色機器人', url: 'https://robohash.org/taichi10?set=set1&size=200x200&bgset=bg2' },
  { id: 11, name: '銀色機器人', url: 'https://robohash.org/taichi11?set=set1&size=200x200&bgset=bg1' },
  { id: 12, name: '金色機器人', url: 'https://robohash.org/taichi12?set=set1&size=200x200&bgset=bg2' },
  
  // 怪物系列 (set2) - 12個
  { id: 13, name: '藍怪物', url: 'https://robohash.org/monster1?set=set2&size=200x200&bgset=bg1' },
  { id: 14, name: '紅怪物', url: 'https://robohash.org/monster2?set=set2&size=200x200&bgset=bg2' },
  { id: 15, name: '綠怪物', url: 'https://robohash.org/monster3?set=set2&size=200x200&bgset=bg1' },
  { id: 16, name: '黃怪物', url: 'https://robohash.org/monster4?set=set2&size=200x200&bgset=bg2' },
  { id: 17, name: '紫怪物', url: 'https://robohash.org/monster5?set=set2&size=200x200&bgset=bg1' },
  { id: 18, name: '橙怪物', url: 'https://robohash.org/monster6?set=set2&size=200x200&bgset=bg2' },
  { id: 19, name: '粉怪物', url: 'https://robohash.org/monster7?set=set2&size=200x200&bgset=bg1' },
  { id: 20, name: '青怪物', url: 'https://robohash.org/monster8?set=set2&size=200x200&bgset=bg2' },
  { id: 21, name: '灰怪物', url: 'https://robohash.org/monster9?set=set2&size=200x200&bgset=bg1' },
  { id: 22, name: '棕怪物', url: 'https://robohash.org/monster10?set=set2&size=200x200&bgset=bg2' },
  { id: 23, name: '深藍怪物', url: 'https://robohash.org/monster11?set=set2&size=200x200&bgset=bg1' },
  { id: 24, name: '深紅怪物', url: 'https://robohash.org/monster12?set=set2&size=200x200&bgset=bg2' },
  
  // 貓咪系列 (set4) - 12個
  { id: 25, name: '藍貓', url: 'https://robohash.org/cat1?set=set4&size=200x200&bgset=bg1' },
  { id: 26, name: '紅貓', url: 'https://robohash.org/cat2?set=set4&size=200x200&bgset=bg2' },
  { id: 27, name: '綠貓', url: 'https://robohash.org/cat3?set=set4&size=200x200&bgset=bg1' },
  { id: 28, name: '黃貓', url: 'https://robohash.org/cat4?set=set4&size=200x200&bgset=bg2' },
  { id: 29, name: '紫貓', url: 'https://robohash.org/cat5?set=set4&size=200x200&bgset=bg1' },
  { id: 30, name: '橙貓', url: 'https://robohash.org/cat6?set=set4&size=200x200&bgset=bg2' },
  { id: 31, name: '粉貓', url: 'https://robohash.org/cat7?set=set4&size=200x200&bgset=bg1' },
  { id: 32, name: '青貓', url: 'https://robohash.org/cat8?set=set4&size=200x200&bgset=bg2' },
  { id: 33, name: '白貓', url: 'https://robohash.org/cat9?set=set4&size=200x200&bgset=bg1' },
  { id: 34, name: '黑貓', url: 'https://robohash.org/cat10?set=set4&size=200x200&bgset=bg2' },
  { id: 35, name: '花貓', url: 'https://robohash.org/cat11?set=set4&size=200x200&bgset=bg1' },
  { id: 36, name: '虎斑貓', url: 'https://robohash.org/cat12?set=set4&size=200x200&bgset=bg2' }
])

// UID 驗證
const validateUid = async () => {
  const uid = formData.value.uid.trim()
  
  if (!uid) {
    uidError.value = '帳號不能為空'
    return
  }
  
  // 檢查格式：只能包含英文字母和數字
  if (!/^[a-zA-Z0-9]+$/.test(uid)) {
    uidError.value = '帳號只能包含英文字母和數字'
    return
  }
  
  // 檢查長度
  if (uid.length < 3 || uid.length > 20) {
    uidError.value = '帳號長度必須在 3-20 個字元之間'
    return
  }
  
  // 檢查唯一性 - 調用 API 檢查
  try {
    const response = await fetch(`/api/v1/users/check_uid?uid=${encodeURIComponent(uid)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })
    
    const result = await response.json()
    
    if (result.available) {
      uidError.value = ''
    } else {
      uidError.value = result.message || '此帳號不可用'
    }
  } catch (error) {
    console.error('UID 驗證失敗:', error)
    uidError.value = '無法驗證帳號，請稍後重試'
  }
}

// 檔案上傳處理
const handleFileUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      alert('請選擇圖片檔案')
      return
    }
    
    // 檢查檔案大小 (限制 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('圖片檔案大小不能超過 5MB')
      return
    }
    
    // 預覽圖片
    const reader = new FileReader()
    reader.onload = (e) => {
      previewAvatar.value = e.target.result
    }
    reader.readAsDataURL(file)
    
    formData.value.avatar = file
  }
}

// 選擇免費頭像
const selectFreeAvatar = (avatar) => {
  selectedFreeAvatar.value = avatar
  previewAvatar.value = avatar.url
  formData.value.avatar = avatar.url
}

// 提交表單
const updateProfile = async () => {
  if (!isFormValid.value) return
  
  isSubmitting.value = true
  
  try {
    // 準備表單資料
    const formDataToSend = new FormData()
    formDataToSend.append('user[uid]', formData.value.uid)
    formDataToSend.append('user[nickname]', formData.value.nickname)
    
    // 處理頭像
    if (avatarType.value === 'upload' && formData.value.avatar instanceof File) {
      formDataToSend.append('user[avatar]', formData.value.avatar)
    } else if (avatarType.value === 'free' && selectedFreeAvatar.value) {
      // 對於免費頭像，我們需要下載圖片並上傳
      // 這裡簡化處理，直接儲存 URL
      formDataToSend.append('user[avatar_url]', selectedFreeAvatar.value.url)
    }
    
    // 發送更新請求到新的更新路由
    const response = await fetch('/users/update_profile', {
      method: 'PATCH',
      body: formDataToSend,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        'Accept': 'application/json'
      }
    })
    
    if (response.ok) {
      const result = await response.json()
      
      if (result.success) {
        // 更新用戶資料
        await userStore.initAuth()
        
        alert(result.message || '個人資料更新成功！')
        goBack()
      } else {
        const errorMessages = result.errors ? result.errors.join(', ') : result.message
        alert(errorMessages || '更新失敗，請稍後重試')
      }
    } else {
      const error = await response.json().catch(() => ({ message: '更新失敗，請稍後重試' }))
      const errorMessages = error.errors ? error.errors.join(', ') : error.message
      alert(errorMessages || '更新失敗，請稍後重試')
    }
  } catch (error) {
    console.error('更新個人資料失敗:', error)
    alert('更新失敗，請稍後重試')
  } finally {
    isSubmitting.value = false
  }
}

// 登出
const logout = async () => {
  if (confirm('確定要登出嗎？')) {
    await userStore.logout()
    goBack()
  }
}

// 返回首頁
const goBack = () => {
  navigateHome()
}

// 導航到首頁的方法
const navigateHome = () => {
  // 更新 URL
  window.history.pushState(null, '', '/')
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/')
  
  console.log('從個人資料頁面返回首頁')
  
  // 清理狀態並載入首頁內容
  postsStore.clearPostDetail()
  postsStore.postlink()
  postsStore.getPosts()
}

// 初始化資料
onMounted(async () => {
  // 確保 userStore 有最新的用戶資訊
  if (!userStore.currentUser) {
    console.log('userStore.currentUser 為空，正在初始化認證...')
    await userStore.initAuth()
  }
  
  console.log('UserProfile 初始化，當前用戶資訊:', {
    currentUser: userStore.currentUser,
    uid: userStore.currentUser?.uid,
    nickname: userStore.currentUser?.nickname,
    name: userStore.currentUser?.name
  })
  
  if (userStore.currentUser) {
    formData.value.uid = userStore.currentUser.uid || ''
    formData.value.nickname = userStore.currentUser.nickname || userStore.currentUser.name || ''
    
    console.log('表單初始化完成:', {
      原始uid: userStore.currentUser.uid,
      表單uid: formData.value.uid,
      nickname: formData.value.nickname,
      isNewUser: isNewUser.value
    })
  } else {
    console.warn('userStore.currentUser 仍為空！')
  }
})
</script>

<style scoped>
/* 繼承既有的樣式系統 */
</style>